import { TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { StepItemProps } from '@net0-calc/common/types';
import { Fragment } from 'react';
import styled from 'styled-components/macro';

interface InlineStepNavProps<T, K> {
  steps: StepItemProps<T, K>[];
  currentStepIndex: number;
  onStepClick: (stepIndex: number) => void;
}

export function InlineStepNav<T, K>({ steps, currentStepIndex, onStepClick }: InlineStepNavProps<T, K>) {
  return (
    <Nav>
      {steps.map((step, index) => (
        <Fragment key={index}>
          {step.icon && (
            <StepPill
              role={index < currentStepIndex ? 'button' : undefined}
              tabIndex={index < currentStepIndex ? 0 : -1}
              title={index > currentStepIndex && step.description ? step.description : undefined}
              $isActive={index === currentStepIndex}
              $isCompleted={index < currentStepIndex}
              $isClickable={index < currentStepIndex}
              onClick={() => index < currentStepIndex && onStepClick(index)}
              onKeyDown={e => {
                if (index >= currentStepIndex) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStepClick(index);
                }
              }}>
              {step.title}
            </StepPill>
          )}
        </Fragment>
      ))}
    </Nav>
  );
}

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StepPill = styled.div<{
  $isActive: boolean;
  $isCompleted: boolean;
  $isClickable: boolean;
}>`
  ${TYPOGRAPHY.captionRegular12}
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  transition: all 0.2s ease;
  flex-shrink: 0;

  color: ${({ $isActive, $isCompleted }) =>
    $isActive ? 'var(--color5)' : $isCompleted ? 'var(--color6)' : 'var(--color9)'};
  background: ${({ $isActive, $isCompleted }) =>
    $isActive ? 'var(--color2)' : $isCompleted ? 'var(--color8)' : 'transparent'};
  border: 1px solid
    ${({ $isActive, $isCompleted }) =>
      $isActive ? 'var(--color2)' : $isCompleted ? 'var(--color10)' : 'var(--color10)'};
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};

  &:hover {
    ${({ $isClickable }) =>
      $isClickable
        ? `
      background: var(--color8);
      border-color: var(--color2);
      color: var(--color2);
    `
        : ''}
  }

  &:focus-visible {
    outline: 2px solid var(--color2);
    outline-offset: 2px;
  }
`;
